<template>
  <div>
    <section-header class="w-full mt-2" title="Quick Replies" @remove-section="removeSection" />
    <div class="text-gray-dark">
      <vs-collapse accordion type="margin" class="px-0" :key="collapseKey">
        <draggable-container :items="quickReplies"
                             :is-disabled="openQrIndex !== -1"
                             @change-order="reorderQuickReplies"
                             @is-dragging="draggingStatus => isDragging = draggingStatus">
          <vs-collapse-item v-for="(qr, i) in quickReplies"
                            :ref="`qr${i}`"
                            not-arrow
                            :open="isOpen(i)"
                            :key="i + collapseKey"
                            class="shadow-none rounded editable-qr my-1 p-0 border-solid border cursor-default"
                            :class="resolveItemClasses(i)">
            <div slot="header"
                 @click="toggleQrOpened(i)"
                 class="text-center text-sm mt-3 mx-5 py-3 editable-qr-header select-none border border-solid rounded-full"
                 :class="resolveHeaderClasses(i)"
                 @mouseover="isHover = true"
                 @mouseleave="isHover = false">
              <span class="text-gray-dark cursor-pointer">
                {{ substituteText(qr.title) }}
              </span>
              <vs-icon v-if="openQrIndex === -1 && isHover"
                       icon="drag_indicator" size="1.5rem"
                       class="absolute left-0 ml-10 text-gray-dark"/>
            </div>

            <quick-reply-editor :ref="`qrEditor${i}`"
                                :quick-reply="qr"
                                :index="i"
                                @remove-quick-reply="() => removeQuickReply(i)"
                                @update-quick-reply="quickReply => updateButton(i, quickReply)"
                                @update-request-outcome="requestOutcome => updateQuickReplyRequestOutcome(i, requestOutcome)"
            />
          </vs-collapse-item>
        </draggable-container>

        <div v-if="quickReplies && quickReplies.length < 11" class="flex mt-4 mx-5 border-dashed border-soft rounded-full cursor-pointer" @click="addQuickReply">
          <vs-icon class="ml-auto my-auto" icon="add"/>
          <span class="my-3 mr-auto text-sm">Add Quick Reply</span>
        </div>
      </vs-collapse>
    </div>
  </div>
</template>

<script>
import { SECTION_FOCUSES } from '@/store/constants'
import contentUtils from '@/utils/content.utils'
import RandomUtils from '@/utils/random.utils'
import SectionHeader from './SectionHeader'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import templateModule from '@/store/modules/template.module'
import templateController from '@/controllers/template.controller'
import answerController from '@/controllers/answer.controller'
import QuickReplyEditor from './QuickReplyEditor.vue'
import assocPath from 'ramda/src/assocPath'
import find from 'ramda/src/find'
import pathEq from 'ramda/src/pathEq'
import DraggableContainer from '../DraggableContainer.vue'
import variableRefsUtils from '@/utils/variable-refs.utils'

const randomString = RandomUtils.randomString

export default {

  components: {
    QuickReplyEditor,
    SectionHeader,
    DraggableContainer
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    actionId() {
      this.closeQrCollapses()
    },

    focus(focus) {
      // PROBLEM: when TextSection is focused and user clicks on button
      // TextSection.handleBlur() sets sectionFocus to NONE, so the watch logic would reset this component before handling the click event
      // HACK: setTimeout to delay the logic until the click event is handled
      setTimeout(() => {
        if (focus?.startsWith(SECTION_FOCUSES.QUICK_REPLY)) {
          const index = chatBubbleModule.getIndexFromFocus(focus, SECTION_FOCUSES.QUICK_REPLY)
          if(this.isOpen(index)) {
            chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
            return
          }

          this.openQuickReply(index)
          return
        }

        this.closeQrCollapses()
      }, 30)
    }

  },

  data() {
    return {
      isDragging: false,
      isHover: false,
      collapseKey: 0,
      openQrIndex: -1
    }
  },

  computed: {
    actionId: chatBubbleModule.actionId,
    focus: chatBubbleModule.sectionFocus,

    stateId() {
      return this.$store.state.ui.preview.bubbleSelected.stateId
    },

    action() {
      return conversationModule.getAction(this.stateId, this.actionId)
    },

    text: {
      get() {
        return contentUtils.getText(this.action)
      }
    },

    buttons: {
      get() {
        return contentUtils.getButtons(this.action)
      }
    },

    quickReplies: {
      get() {
        return contentUtils.getQuickReplies(this.action)
      }
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    }
  },
  methods: {
    resolveItemClasses(i) {
      if (this.isOpen(i)) return ['border-soft']

      const classes = ['border-transparent']
      if (this.isDragging) classes.push('cursor-grabbing')

      return classes
    },

    resolveHeaderClasses(i) {
      if (this.isOpen(i)) return ['mb-3', 'border-focus', 'cursor-pointer']

      const classes = ['border-soft']

      if (this.isDragging && this.openQrIndex === -1) {
        classes.push('cursor-grabbing')
      } else if (this.openQrIndex === -1) {
        classes.push('cursor-grab')
      } else {
        classes.push('cursor-pointer')
      }

      return classes
    },

    reorderQuickReplies(fromIndex, toIndex) {
      this.previewController.reorderQuickReplies(this.stateId, this.actionId, this.text, this.buttons, this.quickReplies, fromIndex, toIndex, templateModule.getTemplateId())
    },

    isOpen(i) {
      return i === this.openQrIndex
    },

    toggleQrOpened(i) {
      if (this.$refs[`qr${i}`]?.[0]) {
        this.$refs[`qr${i}`][0].$refs.content = this.$refs[`qrEditor${i}`]
      }
      if (this.isOpen(i)) {
        chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
        return
      }
      this.$store.commit('UI_BUBBLE_SECTION_FOCUS', SECTION_FOCUSES.QUICK_REPLY + i)
    },

    openQuickReply(i) {
      this.openQrIndex = i
      this.resetCollapseKey()
    },

    addQuickReply() {
      const qrPayload = 'qr_' + this.actionId + '_' + randomString(5)
      const qrTitle = `Quick Reply ${this.quickReplies.length + 1}`
      this.previewController.addQuickReply(this.stateId, this.actionId, qrTitle, qrPayload, templateModule.getTemplateId())
      this.resetCollapseKey()
    },

    updateButton(i, qr) {
      const content = this.action.content
      const newContent = assocPath(['quick_replies', i, 'title'], qr.title, content)
      conversationModule.addOutputAction(this.actionId, newContent)
    },

    updateQuickReplyRequestOutcome(i, requestOutcome) {
      this.previewController.updateQuickReplyRequestOutcome(this.stateId, i, requestOutcome, templateModule.getTemplateId())
    },

    removeQuickReply(i) {
      const qrStateId = this.getQrStateId(this.quickReplies[i].payload)
      this.previewController.removeQuickReply(qrStateId, this.stateId, this.actionId, templateModule.getTemplateId())
      chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
      this.closeQrCollapses()
    },

    removeSection() {
      const qrStateIds = this.quickReplies.map(qr => this.getQrStateId(qr.payload))
      this.previewController.removeQuickReplies(qrStateIds, this.stateId, this.actionId, templateModule.getTemplateId())
      chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
      this.closeQrCollapses()
    },

    getQrStateId(qrPayload) {
      const stateTransitions = conversationModule.getState(this.stateId).transitions
      return find(pathEq(['condition', 'expression', 'equals'], qrPayload), stateTransitions).state
    },

    closeQrCollapses() {
      this.resetCollapseKey()
      this.openQrIndex = -1
    },

    resetCollapseKey() {
      this.collapseKey = Math.random()
    },

    substituteText(text) {
      return variableRefsUtils.substituteVariableRefs(text)
    }
  }
}
</script>

<style scoped lang="scss">

  .editable-qr.open-item {
    // noinspection CssInvalidFunction - theme() belongs to TailWind
    box-shadow: 0 10px 30px theme('colors.gray-a10') !important;
  }

  .editable-qr.open-item header {
    padding: 0.5rem !important;
  }

  .editable-qr.open-item > header > .editable-qr-header {
    border: 1px solid;
    // noinspection CssInvalidFunction - theme() belongs to TailWind
    border-color: theme('colors.focus') !important;
    border-radius: 4px;
  }

</style>
